import { logger } from "../logger-init";

interface UserContext {
  userId?: string;
  email?: string;
  tenantId?: string;
  instanceId?: string;
}

let userContext: UserContext = {};

export function setUserContext(context: UserContext) {
  userContext = { ...userContext, ...context };
  logger.info("User context set", { ...userContext });
}

export function updateInstanceContext(instanceId?: string, tenantId?: string) {
  if (instanceId) userContext.instanceId = instanceId;
  if (tenantId) userContext.tenantId = tenantId;
}

export function clearUserContext() {
  userContext = {};
}

export function logPageView(
  pageName: string,
  path: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("Page view", {
    event: "page_view",
    pageName,
    path,
    ...userContext,
    ...additionalData,
  });
}

export function logClick(
  elementName: string,
  elementType: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("User click", {
    event: "click",
    elementName,
    elementType,
    ...userContext,
    ...additionalData,
  });
}

function logButtonClick(buttonName: string, additionalData?: Record<string, unknown>) {
  logClick(buttonName, "button", additionalData);
}

function logLinkClick(linkName: string, href: string, additionalData?: Record<string, unknown>) {
  logClick(linkName, "link", { href, ...additionalData });
}

function logFormSubmit(formName: string, additionalData?: Record<string, unknown>) {
  logger.info("Form submit", {
    event: "form_submit",
    formName,
    ...userContext,
    ...additionalData,
  });
}

function logFormError(
  formName: string,
  errors: Record<string, string>,
  additionalData?: Record<string, unknown>,
) {
  logger.warn("Form validation error", {
    event: "form_error",
    formName,
    errors,
    ...userContext,
    ...additionalData,
  });
}

function logUserAction(action: string, category: string, additionalData?: Record<string, unknown>) {
  logger.info("User action", {
    event: "user_action",
    action,
    category,
    ...userContext,
    ...additionalData,
  });
}

function logApiCall(
  method: string,
  endpoint: string,
  status: number,
  durationMs: number,
  additionalData?: Record<string, unknown>,
) {
  const logMethod = status >= 400 ? logger.warn : logger.info;
  logMethod.call(logger, "API call", {
    event: "api_call",
    method,
    endpoint,
    status,
    durationMs,
    ...userContext,
    ...additionalData,
  });
}

function logError(errorMessage: string, error?: Error, additionalData?: Record<string, unknown>) {
  logger.error("Application error", {
    event: "error",
    errorMessage,
    stack: error?.stack,
    ...userContext,
    ...additionalData,
  });
}

function logSearch(
  searchTerm: string,
  resultCount: number,
  additionalData?: Record<string, unknown>,
) {
  logger.info("Search performed", {
    event: "search",
    searchTerm,
    resultCount,
    ...userContext,
    ...additionalData,
  });
}

function logTableAction(
  action: string,
  tableName: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("Table action", {
    event: "table_action",
    action,
    tableName,
    ...userContext,
    ...additionalData,
  });
}

function logModalOpen(modalName: string, additionalData?: Record<string, unknown>) {
  logger.info("Modal opened", {
    event: "modal_open",
    modalName,
    ...userContext,
    ...additionalData,
  });
}

function logModalClose(modalName: string, additionalData?: Record<string, unknown>) {
  logger.info("Modal closed", {
    event: "modal_close",
    modalName,
    ...userContext,
    ...additionalData,
  });
}

function logTabChange(
  tabName: string,
  previousTab?: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("Tab changed", {
    event: "tab_change",
    tabName,
    previousTab,
    ...userContext,
    ...additionalData,
  });
}

function logDropdownSelect(
  dropdownName: string,
  selectedValue: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("Dropdown selection", {
    event: "dropdown_select",
    dropdownName,
    selectedValue,
    ...userContext,
    ...additionalData,
  });
}

function logFileUpload(
  fileName: string,
  fileSize: number,
  fileType: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("File upload", {
    event: "file_upload",
    fileName,
    fileSize,
    fileType,
    ...userContext,
    ...additionalData,
  });
}

function logLogin(success: boolean, method: string, additionalData?: Record<string, unknown>) {
  const logMethod = success ? logger.info : logger.warn;
  logMethod.call(logger, success ? "Login successful" : "Login failed", {
    event: "login",
    success,
    method,
    ...userContext,
    ...additionalData,
  });
}

function logLogout(additionalData?: Record<string, unknown>) {
  logger.info("User logout", {
    event: "logout",
    ...userContext,
    ...additionalData,
  });
}

function logTicketAction(
  action: string,
  ticketId: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("Ticket action", {
    event: "ticket_action",
    action,
    ticketId,
    ...userContext,
    ...additionalData,
  });
}

function logKBArticleView(
  articleId: string,
  articleTitle: string,
  additionalData?: Record<string, unknown>,
) {
  logger.info("KB article viewed", {
    event: "kb_article_view",
    articleId,
    articleTitle,
    ...userContext,
    ...additionalData,
  });
}
